import { useState, useEffect, useCallback } from 'react';

interface ThemeHook {
  theme: 'light' | 'dark';
  toggleTheme: () => void;
  isThemeEnabled: boolean;
}

const isDarkModeEnabled = import.meta.env.VITE_ENABLE_DARK_MODE === 'true';

export const useTheme = (): ThemeHook => {
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    if (!isDarkModeEnabled) {
      return 'light';
    }
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | null;
    const userPrefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;

    return savedTheme || (userPrefersDark ? 'dark' : 'light');
  });

  useEffect(() => {
    if (!isDarkModeEnabled) {
      document.documentElement.classList.remove('dark');
      document.documentElement.classList.add('light');
      localStorage.removeItem('theme'); // Limpa qualquer tema salvo anteriormente
      return;
    }

    const root = window.document.documentElement;
    root.classList.remove(theme === 'dark' ? 'light' : 'dark');
    root.classList.add(theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    if (!isDarkModeEnabled) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setTheme(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);


  const toggleTheme = useCallback(() => {
    if (isDarkModeEnabled) {
      setTheme((prevTheme) => (prevTheme === 'dark' ? 'light' : 'dark'));
    }
  }, []);

  return { theme, toggleTheme, isThemeEnabled: isDarkModeEnabled };
};